import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic numeric range facet bound to a store path holding `{gte?, lte?}`.
 *
 * Matches the backend's range-filter contract (see
 * ElasticsearchEndpointController::buildUserFilterClause): an object with the
 * comparison operators it understands. Empty bounds are omitted, so an
 * untouched range writes `{}` (no clause).
 *
 * Two layouts:
 *   - mode: range  → a "from" and a "to" number input (price, year…).
 *   - mode: min    → a single lower bound. With `presets`, renders buttons
 *                    (e.g. "4★ et plus" → {gte: 4}); without, a number input.
 *
 * Like FacetList, the refetch lives in the YAML `actions:` and fires on the
 * bubbled event. Number inputs commit to the store on change and the YAML
 * should refetch `on: blur` to avoid a request per keystroke; preset buttons
 * refetch `on: click`.
 *
 * YAML usage:
 *   - type: RangeFacet
 *     dataLocation: ~~.selectedFilters.by_price
 *     mode: range
 *     min: 0
 *     max: 5000
 *     step: 50
 *     prefix: ''
 *     suffix: ' €'
 *     actions:
 *       - { what: setData, on: blur, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: blur, ... }
 */

export const RangeFacet = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)

  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const mode = resolve(props.mode) || 'range'
  const isMin = mode === 'min'
  const min = resolve(props.min)
  const max = resolve(props.max)
  const step = resolve(props.step) ?? 1
  const minLabel = resolve(props.minLabel) ?? 'De'
  const maxLabel = resolve(props.maxLabel) ?? 'À'
  const prefix = resolve(props.prefix) ?? ''
  const suffix = resolve(props.suffix) ?? ''
  const presets = resolve(props.presets)
  // Optional unit multiplier between the UI value and the stored bound — e.g.
  // `scale: 1000` lets the user type milliseconds while the filter stores
  // microseconds. Defaults to 1 (no conversion, fully back-compatible).
  const scale = Number(resolve(props.scale)) || 1

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  const range = formData && typeof formData === 'object' ? formData : {}

  // Writes a {gte?, lte?} object, dropping empty / non-numeric bounds.
  const writeBounds = (gte, lte) => {
    const next = {}
    if (gte !== '' && gte != null && !Number.isNaN(Number(gte))) next.gte = Number(gte) * scale
    if (lte !== '' && lte != null && !Number.isNaN(Number(lte))) next.lte = Number(lte) * scale
    globalDataContext.updateData(next, formDataPath)
  }

  // Stored bound → UI value (undo the scale for display).
  const displayBound = (v) => (v == null ? '' : v / scale)

  // Style overrides (all optional, defaults reproduce the original look):
  //   containerClass — wrapper padding/layout ('p-4' by default)
  //   inputClass     — the number input(s) class
  //   unitLabel      — a short unit shown after the input in `min` mode ('ms'…)
  //   placeholder    — explicit placeholder (else built from prefix+min+suffix)
  const numberInputClass =
    'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:ring-2 focus:ring-indigo-400'
  const containerClass = resolve(props.containerClass) ?? 'p-4'
  const inputClass = resolve(props.inputClass) ?? numberInputClass
  const unitLabel = resolve(props.unitLabel)
  const minPlaceholder = resolve(props.placeholder) ?? (prefix + (min ?? '') + suffix)
  const maxPlaceholder = resolve(props.placeholder) ?? (prefix + (max ?? '') + suffix)

  // ── Preset buttons (e.g. rating "≥ N") ──────────────────────────────────
  if (isMin && Array.isArray(presets)) {
    const current = range.gte
    return (
      <ActionDependant {...props} attributesHolderRef={containerRef}>
        <div ref={containerRef} className="p-1.5 space-y-0.5">
          {presets.map((p) => {
            const selected = (p.value === '' || p.value == null) ? (current == null) : Number(current) === Number(p.value) * scale
            return (
              <button
                key={String(p.value)}
                type="button"
                onClick={() => {
                  if (p.value === '' || p.value == null) globalDataContext.updateData({}, formDataPath)
                  else writeBounds(p.value, '')
                }}
                className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  selected ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span>{p.label}</span>
              </button>
            )
          })}
        </div>
      </ActionDependant>
    )
  }

  // ── Number inputs ───────────────────────────────────────────────────────
  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className={containerClass}>
        <div className={isMin ? 'flex items-center gap-1.5' : 'grid grid-cols-2 gap-3'}>
          <label className="block">
            {minLabel ? (
              <span className="block text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider font-semibold">
                {minLabel}
              </span>
            ) : null}
            <input
              type="number"
              value={displayBound(range.gte)}
              min={min}
              max={max}
              step={step}
              placeholder={minPlaceholder}
              onChange={(e) => writeBounds(e.target.value, displayBound(range.lte))}
              className={inputClass}
            />
          </label>
          {isMin && unitLabel ? (
            <span className="text-sm text-gray-500">{unitLabel}</span>
          ) : null}
          {!isMin && (
            <label className="block">
              <span className="block text-[10px] text-gray-400 mb-1.5 uppercase tracking-wider font-semibold">
                {maxLabel}
              </span>
              <input
                type="number"
                value={displayBound(range.lte)}
                min={min}
                max={max}
                step={step}
                placeholder={maxPlaceholder}
                onChange={(e) => writeBounds(displayBound(range.gte), e.target.value)}
                className={inputClass}
              />
            </label>
          )}
        </div>
      </div>
    </ActionDependant>
  )
}
